import { useState } from 'react';
import { Lock, Sparkles, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Free-Play Wager Progress Badge.
 *
 * Renders on the Lobby ONLY when the player has an active rollover
 * requirement (i.e., they've received a free-play bonus that hasn't
 * been wagered through yet). Auto-hides at 0 / 0.
 *
 * Reads `bonus_credits`, `pending_rollover`, `pending_rollover_total`
 * from the user object (already polled every 20s via AuthContext).
 *
 * Visual goal: kill the most common support question — "why can't I
 * cash this out?" — by making the wager-through requirement explicit
 * and progress-driven instead of hidden in transaction history.
 */
const FreePlayProgressBadge = ({ user, compact = false }) => {
    const [expanded, setExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const bonusCredits = Number(user?.bonus_credits || 0);
    const pending = Number(user?.pending_rollover || 0);
    const pendingTotal = Number(user?.pending_rollover_total || 0);

    // Hide when there's no active rollover OR nothing to unlock.
    if (dismissed) return null;
    if (pendingTotal <= 0 && bonusCredits <= 0) return null;

    const wagered = Math.max(0, pendingTotal - pending);
    const pct = pendingTotal > 0 ? Math.min(100, Math.round((wagered / pendingTotal) * 100)) : 100;
    const remaining = Math.max(0, pending);

    // Fully wagered through? Bonus is about to convert (or already has).
    const cleared = pending <= 0 && pendingTotal > 0;

    // ─────────────────────────────────────────────────────────────────
    // Compact variant — slim one-line strip designed for the Sports
    // betslip header and Slots transfer dialog. No info/dismiss
    // controls (saves precious vertical space in those contexts).
    // ─────────────────────────────────────────────────────────────────
    if (compact) {
        return (
            <div
                className="rounded-lg px-3 py-2 border"
                style={{
                    background: cleared
                        ? 'linear-gradient(135deg, rgba(57,255,20,0.10), rgba(255,215,0,0.06))'
                        : 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(255,77,184,0.04))',
                    borderColor: cleared ? 'rgba(57,255,20,0.40)' : 'rgba(168,85,247,0.40)',
                }}
                data-testid="freeplay-progress-compact"
            >
                <div className="flex items-center gap-2 mb-1.5">
                    {cleared
                        ? <Sparkles className="w-3.5 h-3.5 text-[#39FF14] shrink-0" />
                        : <Lock className="w-3.5 h-3.5 text-[#A855F7] shrink-0" />}
                    <p className="text-[10px] font-black uppercase tracking-wider text-white">
                        {cleared ? 'Free play unlocked!' : 'Free play wager'}
                    </p>
                    <p className="text-[10px] text-[#A0AAB2] ml-auto tabular-nums">
                        {cleared
                            ? `$${bonusCredits.toFixed(2)} ready`
                            : `$${wagered.toFixed(0)} / $${pendingTotal.toFixed(0)} · $${remaining.toFixed(2)} to go`}
                    </p>
                </div>
                <div className="relative h-1.5 rounded-full bg-black/40 overflow-hidden">
                    <motion.div
                        className="h-full rounded-full"
                        style={{
                            background: cleared
                                ? 'linear-gradient(90deg, #39FF14, #FFD700)'
                                : 'linear-gradient(90deg, #A855F7, #FF4DB8)',
                        }}
                        initial={{ width: '0%' }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        data-testid="freeplay-progress-compact-fill"
                    />
                </div>
            </div>
        );
    }

    return (
        <AnimatePresence>
            <motion.div
                key="freeplay-progress"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="relative w-full overflow-hidden rounded-2xl border p-3 sm:p-4"
                style={{
                    background: cleared
                        ? 'linear-gradient(135deg, rgba(57,255,20,0.12) 0%, rgba(255,215,0,0.08) 100%)'
                        : 'linear-gradient(135deg, rgba(168,85,247,0.10) 0%, rgba(255,77,184,0.08) 100%)',
                    borderColor: cleared ? 'rgba(57,255,20,0.40)' : 'rgba(168,85,247,0.40)',
                }}
                data-testid="freeplay-progress-badge"
            >
                <div className="flex items-center gap-3">
                    <div
                        className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center"
                        style={{
                            background: cleared ? 'rgba(57,255,20,0.20)' : 'rgba(168,85,247,0.20)',
                            border: cleared ? '1px solid rgba(57,255,20,0.55)' : '1px solid rgba(168,85,247,0.55)',
                        }}
                    >
                        {cleared
                            ? <Sparkles className="w-5 h-5 text-[#39FF14]" />
                            : <Lock className="w-5 h-5 text-[#A855F7]" />}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-white">
                                {cleared ? 'Free play unlocked!' : 'Free play wager progress'}
                            </p>
                            <button
                                type="button"
                                onClick={() => setExpanded(v => !v)}
                                className="text-[#A0AAB2] hover:text-white"
                                title="How does this work?"
                                data-testid="freeplay-progress-info"
                            >
                                <Info className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setDismissed(true)}
                                className="ml-auto text-[#A0AAB2] hover:text-white"
                                title="Hide for this session"
                                data-testid="freeplay-progress-dismiss"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Progress bar */}
                        <div className="relative h-2 rounded-full bg-black/40 overflow-hidden mb-1">
                            <motion.div
                                className="h-full rounded-full"
                                style={{
                                    background: cleared
                                        ? 'linear-gradient(90deg, #39FF14, #FFD700)'
                                        : 'linear-gradient(90deg, #A855F7, #FF4DB8)',
                                }}
                                initial={{ width: '0%' }}
                                animate={{ width: `${pct}%` }}
                                transition={{ duration: 0.6, ease: 'easeOut' }}
                                data-testid="freeplay-progress-fill"
                            />
                        </div>

                        <p className="text-[11px] sm:text-xs text-[#E0E0E0] tabular-nums" data-testid="freeplay-progress-text">
                            {cleared ? (
                                <>
                                    <span className="text-[#39FF14] font-bold">${bonusCredits.toFixed(2)}</span> ready to be released to your cashable balance.
                                </>
                            ) : (
                                <>
                                    <span className="text-white font-bold">${wagered.toFixed(2)}</span>
                                    <span className="text-[#A0AAB2]"> of </span>
                                    <span className="text-white font-bold">${pendingTotal.toFixed(2)}</span>
                                    <span className="text-[#A0AAB2]"> wagered </span>
                                    <span className="text-[#A855F7] font-bold">· ${remaining.toFixed(2)} to go</span>
                                </>
                            )}
                        </p>
                        {!cleared && (
                            <p className="text-[10px] sm:text-[11px] text-[#A0AAB2] mt-0.5" data-testid="freeplay-progress-hint">
                                Every slot, table, sports and lotto bet counts. Your cash and all winnings are always cashable. Tap ⓘ for details.
                            </p>
                        )}
                    </div>
                </div>

                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-3 pt-3 border-t border-white/10 text-[11px] text-[#A0AAB2] leading-relaxed"
                        data-testid="freeplay-progress-explain"
                    >
                        <p className="mb-1">
                            Your <b className="text-white">free play bonus</b> sits in a separate "bonus" wallet. To turn what is left of it into cash:
                        </p>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Bet on any slot, table game, sports or lotto (cash or bonus — it all counts). Bonus is spent first.</li>
                            <li>All winnings go straight to your cash balance. When your wager total hits the goal, any remaining bonus <b className="text-white">automatically converts</b> to cash.</li>
                            <li>Your cash balance can always be cashed out — only the bonus waits.</li>
                        </ul>
                    </motion.div>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default FreePlayProgressBadge;
